'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

export interface Movie {
  id: string;
  title: string;
  year: number;
  genre: string[];
  rating: number;
  reviewCount: number;
  poster: string;
  posterAlt: string;
  badge?: 'new' | 'trending' | 'staff-pick' | 'top-rated';
  runtime?: number;
  director?: string;
}

interface MovieCardProps {
  movie: Movie;
  size?: 'sm' | 'md' | 'lg';
}

const badgeConfig = {
  new: { label: 'New', className: 'badge-new text-white' },
  trending: { label: 'Trending', className: 'badge-trending' },
  'staff-pick': { label: 'Staff Pick', className: 'badge-staff-pick text-white' },
  'top-rated': { label: 'Top Rated', className: 'bg-gold text-accent-foreground' },
};

export default function MovieCard({ movie, size = 'md' }: MovieCardProps) {
  const [watchlisted, setWatchlisted] = useState(false);
  const [hovered, setHovered] = useState(false);

  const sizeClasses = {
    sm: 'w-36',
    md: 'w-44',
    lg: 'w-52',
  };

  return (
    <div
      className={`relative ${sizeClasses[size]} flex-shrink-0 group movie-card-hover rounded-xl overflow-hidden cursor-pointer`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <Link href="/movie-detail">
        {/* Poster */}
        <div className={`relative ${size === 'sm' ? 'h-52' : size === 'lg' ? 'h-72' : 'h-64'} rounded-xl overflow-hidden bg-muted`}>
          <AppImage
            src={movie.poster}
            alt={movie.posterAlt}
            fill
            className="object-cover transition-transform duration-300 group-hover:scale-105"
            sizes="(max-width: 768px) 144px, 176px"
          />

          {/* Gradient overlay on hover */}
          <div className={`absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent transition-opacity duration-250 ${hovered ? 'opacity-100' : 'opacity-0'}`} />

          {/* Badge */}
          {movie.badge && (
            <div className={`absolute top-2 left-2 px-2 py-0.5 rounded-md text-xs font-700 ${badgeConfig[movie.badge].className}`}>
              {badgeConfig[movie.badge].label}
            </div>
          )}

          {/* Rating badge */}
          <div className="absolute top-2 right-2 flex items-center gap-1 bg-black/70 rounded-md px-1.5 py-0.5">
            <Icon name="StarIcon" variant="solid" size={10} className="rating-star-filled" />
            <span className="text-xs font-700 text-accent font-mono">{movie.rating.toFixed(1)}</span>
          </div>

          {/* Hover overlay content */}
          <div className={`absolute bottom-0 left-0 right-0 p-3 transition-all duration-250 ${hovered ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}`}>
            <p className="text-xs text-muted-foreground mb-1.5">{movie.genre.slice(0, 2).join(' · ')}</p>
            {movie.runtime && (
              <p className="text-xs text-muted-foreground">{movie.runtime}m · {movie.year}</p>
            )}
          </div>
        </div>
      </Link>

      {/* Watchlist button */}
      <button
        onClick={() => setWatchlisted(!watchlisted)}
        className={`absolute top-2 right-2 w-7 h-7 rounded-full flex items-center justify-center transition-all duration-150 ${
          hovered ? 'opacity-100 scale-100' : 'opacity-0 scale-90'
        } ${watchlisted ? 'bg-primary text-white' : 'bg-black/60 text-white hover:bg-primary'}`}
        style={{ top: '2.25rem' }}
        aria-label={watchlisted ? 'Remove from watchlist' : 'Add to watchlist'}
      >
        <Icon name={watchlisted ? 'BookmarkSolidIcon' as Parameters<typeof Icon>[0]['name'] : 'BookmarkIcon'} size={12} />
      </button>

      {/* Title + meta */}
      <div className="mt-2 px-0.5">
        <Link href="/movie-detail">
          <h3 className="text-sm font-600 text-foreground line-clamp-2 leading-snug hover:text-accent transition-colors">
            {movie.title}
          </h3>
        </Link>
        <div className="flex items-center gap-1.5 mt-1">
          <span className="text-xs text-muted-foreground">{movie.year}</span>
          <span className="text-muted-foreground text-xs">·</span>
          <span className="text-xs text-muted-foreground">{movie.reviewCount.toLocaleString()} reviews</span>
        </div>
      </div>
    </div>
  );
}